<template>
  <div class="btn-group search-workgroup">
    <button
      ref="toggle"
      type="button"
      class="btn btn-dropdown dropdown-toggle"
      :class="{ 'is-loading': loading }"
      data-bs-toggle="dropdown"
      data-bs-display="static"
      data-bs-auto-close="outside"
      aria-expanded="false"
      style="min-width: 300px"
    >
      {{ buttonLabel }}
      <span v-if="loading" class="spinner-border spinner-border-sm ms-1" role="status" aria-hidden="true" />
    </button>
    <div class="dropdown-menu p-0 m-0 dropdown-menu-end bg-white overflow-hidden workgroup-menu" style="width: 300px">
      <div class="border-bottom vstack gap-2 p-3 py-2">
        <div class="position-relative mb-2">
          <i class="fa-solid fa-magnifying-glass search-icon position-absolute top-50 translate-middle-y" />
          <input
            :value="searchText"
            type="search"
            class="form-control search-input"
            :placeholder="$t('flexigo.routeList.routeGroupName')"
            @input="onSearchInput($event.target.value)"
          />
        </div>

        <div class="form-check form-switch d-flex align-items-center gap-2 ps-0 small">
          <input
            id="workgroup-search-hide-inactive"
            v-model="hideInactive"
            :disabled="loading"
            class="form-check-input inactive-switch m-0"
            type="checkbox"
            role="switch"
          />
          <label class="form-check-label" for="workgroup-search-hide-inactive">
            {{ $t('flexigo.dashboard.hideInactiveRouteGroups') }}
          </label>
        </div>
      </div>

      <div v-if="isOpen" ref="list" class="item-list mt-2" style="max-height: calc(100vh - 350px)">
        <div
          v-for="{ campus, workgroups: campusWorkgroups, shownRows } in renderedGroups"
          :key="campus.id"
          class="px-3"
        >
          <div class="campus-header d-flex align-items-center gap-2 pb-2">
            <input
              :id="`workgroup-search-campus-${campus.id}`"
              type="checkbox"
              class="form-check-input fs-4 m-0"
              :checked="campusSelection[campus.id] === 'all'"
              :indeterminate.prop="campusSelection[campus.id] === 'some'"
              @change="toggleCampus(campusWorkgroups, $event.target.checked)"
            />
            <label class="form-check-label campus-name" :for="`workgroup-search-campus-${campus.id}`" role="button">
              {{ campus.title }}
            </label>
            <i
              class="fa-solid has-cursor-pointer collapse-icon"
              :class="isCollapsed(campus.id) ? 'fa-chevron-up' : 'fa-chevron-down'"
              @click="toggleCollapse(campus.id)"
            />
          </div>

          <div v-if="!isCollapsed(campus.id)">
            <template v-for="row in shownRows">
              <!-- Instances of one template (e.g. a recurring template's periods), grouped under its name -->
              <div v-if="row.type === 'template'" :key="row.key" class="d-flex align-items-center gap-2 mb-2 ps-3">
                <input
                  :id="row.domId"
                  type="checkbox"
                  class="form-check-input fs-4 m-0"
                  :checked="selectionState(row.workgroups) === 'all'"
                  :indeterminate.prop="selectionState(row.workgroups) === 'some'"
                  @change="toggleCampus(row.workgroups, $event.target.checked)"
                />
                <label class="form-check-label flex-grow-1" :for="row.domId" role="button">
                  {{ row.name }} <span class="text-primary small">({{ row.workgroups.length }})</span>
                </label>
                <i
                  class="fa-solid has-cursor-pointer collapse-icon"
                  :class="isTemplateGroupOpen(row.key) ? 'fa-chevron-down' : 'fa-chevron-up'"
                  @click="toggleTemplateGroup(row.key)"
                />
              </div>
              <div
                v-else
                :key="`workgroup-${row.workgroup.id}`"
                class="d-flex align-items-start gap-2 mb-2"
                :class="row.nested ? 'ps-5' : 'ps-3'"
              >
                <input
                  :id="`workgroup-search-workgroup-${row.workgroup.id}`"
                  type="checkbox"
                  class="form-check-input fs-4 m-0"
                  :checked="selectedIdSet.has(row.workgroup.id)"
                  @change="toggleWorkgroup(row.workgroup.id, $event.target.checked)"
                />
                <label class="form-check-label" :for="`workgroup-search-workgroup-${row.workgroup.id}`" role="button">
                  {{ row.workgroup.name }}
                </label>
              </div>
            </template>
          </div>
        </div>

        <!-- Scrolling this into view renders the next batch of rows. -->
        <div v-if="hasMoreRows" ref="sentinel" class="text-center py-2">
          <span class="spinner-border spinner-border-sm text-secondary" role="status" aria-hidden="true" />
        </div>
      </div>

      <div class="border-top d-flex justify-content-end gap-3 p-3">
        <button
          type="button"
          class="btn btn-outline-primary clear-button"
          :disabled="!selectedIds.length"
          @click="selectedIds = []"
        >
          {{ $t('flexigo.common.clearSelection') }}
        </button>
        <!-- Disabled while loading: a selection restored from the URL has no rows to match yet. -->
        <button type="button" class="btn btn-primary" :disabled="loading" @click="apply">
          {{ $t('flexigo.employee.dashboard.apply') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { Dropdown } from 'bootstrap';
import { debounce } from '@/helpers/utility';
import isWorkgroupInRange from '../helpers/workgroupPeriod';

// Workgroup statuses hidden by the "Hide inactive route groups" switch. Deleting a workgroup cancels its instances.
const INACTIVE_STATUSES = ['CANCELLED', 'ARCHIVED'];
const SEARCH_DEBOUNCE_MS = 150;
// Rows (campus headers + route groups) rendered per batch. The list grows by a batch as it's scrolled.
const ROWS_PER_BATCH = 100;

/**
 * Campus → route group picker. Groups the workgroup instances (from workgroups/instance/prm) under
 * their responsible campus and emits the selected ones on Apply.
 */
export default {
  name: 'WorkgroupSearch',
  props: {
    // Workgroup instances as returned by workgroupService.getWorkgroups().
    workgroups: {
      type: Array,
      default: () => [],
    },
    // Workgroups to show as selected, in the shape this component emits.
    selected: {
      type: Array,
      default: () => [],
    },
    // { fromDate, toDate } ('yyyy-MM-dd'): list only the workgroups whose period overlaps it. Null lists
    // every workgroup.
    dateRange: {
      type: Object,
      default: null,
    },
    // Shows a spinner in place of the toggle arrow while the workgroups are being fetched.
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      // searchText follows the input; filterText trails it by SEARCH_DEBOUNCE_MS and drives the list.
      searchText: '',
      filterText: '',
      // On by default (VSER-15242); applied inactive route groups stay listed regardless, see groups.
      hideInactive: true,
      // The list is only rendered while the dropdown is open, so a closed dropdown costs no DOM.
      isOpen: false,
      rowLimit: ROWS_PER_BATCH,
      selectedIds: [],
      collapsedCampusIds: [],
      // Template groups ("<campusId>:<templateId>") the user opened; they start collapsed.
      openTemplateGroups: [],
    };
  },
  computed: {
    campuses() {
      return this.$store.getters.campuses();
    },
    // The workgroups whose period overlaps dateRange. Applied ones stay listed even outside the range, so
    // they can't be selected unseen (a dashboard drops them from its selection when its range changes).
    workgroupsInRange() {
      const { fromDate, toDate } = this.dateRange || {};
      if (!fromDate || !toDate) return this.workgroups;
      return this.workgroups.filter(
        (workgroup) => this.appliedIdSet.has(workgroup.id) || isWorkgroupInRange(workgroup, { fromDate, toDate }),
      );
    },
    // The applied selection. Applied route groups stay listed even when the date range or the inactive switch
    // would hide them, so they're never selected unseen (e.g. inactive ones restored from the URL).
    appliedIdSet() {
      return new Set((this.selected || []).map(({ id }) => id));
    },
    // Every campus that has at least one (non-hidden) workgroup, with its workgroups.
    groups() {
      const workgroupsByCampus = new Map();
      this.workgroupsInRange.forEach(({ id, name, responsibleCampusId, workgroupStatus, templateId, templateName }) => {
        if (this.hideInactive && INACTIVE_STATUSES.includes(workgroupStatus) && !this.appliedIdSet.has(id)) return;
        if (!workgroupsByCampus.has(responsibleCampusId)) workgroupsByCampus.set(responsibleCampusId, []);
        workgroupsByCampus
          .get(responsibleCampusId)
          .push({ id, name, campusId: responsibleCampusId, templateId, templateName });
      });

      return this.campuses
        .filter(({ id }) => workgroupsByCampus.has(id))
        .map(({ id, title }) => ({ campus: { id, title }, workgroups: workgroupsByCampus.get(id) }));
    },
    // A search matching campus titles shows those campuses whole; otherwise it filters workgroups by their name
    // or their template's, since a template row shows the template name.
    visibleGroups() {
      const text = this.filterText.trim().toLowerCase();
      if (!text) return this.groups;

      const includesText = (value) => value?.toLowerCase().includes(text);
      const matchingCampuses = this.groups.filter(({ campus }) => includesText(campus.title));
      if (matchingCampuses.length) return matchingCampuses;

      return this.groups
        .map((group) => ({
          ...group,
          workgroups: group.workgroups.filter(
            ({ name, templateName }) => includesText(name) || includesText(templateName),
          ),
        }))
        .filter(({ workgroups: campusWorkgroups }) => campusWorkgroups.length);
    },
    // visibleGroups with each campus's route groups laid out as rows: instances of the same template (e.g. a
    // recurring template's periods) under one template row, the rest as plain rows. Route groups without a
    // templateId (an older backend) are all plain rows. A collapsed campus has no rows; a collapsed template
    // group only its template row.
    campusRows() {
      return this.visibleGroups.map((group) => {
        if (this.isCollapsed(group.campus.id)) return { ...group, rows: [] };
        const byTemplate = new Map();
        group.workgroups.forEach((workgroup) => {
          const key = workgroup.templateId ?? `workgroup-${workgroup.id}`;
          if (!byTemplate.has(key)) byTemplate.set(key, []);
          byTemplate.get(key).push(workgroup);
        });
        const rows = [];
        byTemplate.forEach((workgroups, templateKey) => {
          if (workgroups.length === 1) {
            rows.push({ type: 'workgroup', workgroup: workgroups[0] });
            return;
          }
          const key = `${group.campus.id}:${templateKey}`;
          const name = workgroups[0].templateName || workgroups[0].name;
          rows.push({
            type: 'template',
            key,
            name,
            workgroups,
            domId: `workgroup-search-template-${group.campus.id}-${templateKey}`,
          });
          if (!this.isTemplateGroupOpen(key)) return;
          workgroups.forEach((workgroup) => rows.push({ type: 'workgroup', workgroup, nested: true }));
        });
        return { ...group, rows };
      });
    },
    // campusRows cut down to rowLimit rows, campus headers included. Each campus keeps its full `workgroups`
    // so its checkbox still covers route groups that haven't been rendered yet; `shownRows` is what's drawn.
    renderedGroups() {
      let remaining = this.rowLimit;
      const rendered = [];
      this.campusRows.forEach((group) => {
        if (remaining <= 0) return;
        remaining -= 1;
        const shownRows = group.rows.slice(0, remaining);
        remaining -= shownRows.length;
        rendered.push({ ...group, shownRows });
      });
      return rendered;
    },
    hasMoreRows() {
      const countRows = (groups, key) => groups.reduce((total, group) => total + 1 + group[key].length, 0);
      return countRows(this.renderedGroups, 'shownRows') < countRows(this.campusRows, 'rows');
    },
    selectedIdSet() {
      return new Set(this.selectedIds);
    },
    // 'all' | 'some' | 'none' per visible campus, worked out once per selection change.
    campusSelection() {
      return Object.fromEntries(
        this.visibleGroups.map(({ campus, workgroups }) => {
          const selectedCount = workgroups.filter(({ id }) => this.selectedIdSet.has(id)).length;
          if (selectedCount === workgroups.length) return [campus.id, 'all'];
          return [campus.id, selectedCount ? 'some' : 'none'];
        }),
      );
    },
    buttonLabel() {
      const count = this.selectedIds.length;
      return count
        ? this.$tc('flexigo.dashboard.routeGroupSelected', count, { count })
        : this.$t('flexigo.dashboard.allCampuses');
    },
  },
  watch: {
    selected: {
      immediate: true,
      handler(selected) {
        this.selectedIds = (selected || []).map(({ id }) => id);
      },
    },
    filterText() {
      this.resetRows();
    },
    // Hidden workgroups can't stay selected, or the count would include rows the user can't see. Only
    // route groups known to be inactive are dropped, so ids not loaded (yet) survive the switch.
    // Only while open, i.e. when the user turns the switch on: closing without Apply also puts the switch
    // back, and that must not drop the applied (restored) selection's inactive route groups.
    hideInactive(hide) {
      this.resetRows();
      if (!hide || !this.isOpen) return;
      const inactiveIds = new Set(
        this.workgroups
          .filter(({ workgroupStatus }) => INACTIVE_STATUSES.includes(workgroupStatus))
          .map(({ id }) => id),
      );
      this.selectedIds = this.selectedIds.filter((id) => !inactiveIds.has(id));
    },
  },
  created() {
    this.applySearch = debounce((text) => {
      this.filterText = text;
    }, SEARCH_DEBOUNCE_MS);
  },
  mounted() {
    this.$refs.toggle.addEventListener('show.bs.dropdown', this.onShow);
    this.$refs.toggle.addEventListener('hidden.bs.dropdown', this.onHidden);
  },
  // Re-observing after every render makes the observer report the sentinel's current position, so a
  // batch that still leaves the sentinel in view (short rows, collapsed campuses) pulls in the next one.
  updated() {
    this.observeSentinel();
  },
  beforeDestroy() {
    this.$refs.toggle.removeEventListener('show.bs.dropdown', this.onShow);
    this.$refs.toggle.removeEventListener('hidden.bs.dropdown', this.onHidden);
    this.stopObserving();
  },
  methods: {
    onShow() {
      this.rowLimit = ROWS_PER_BATCH;
      this.isOpen = true;
      this.hideInactiveOnOpen = this.hideInactive;
    },
    onHidden() {
      this.isOpen = false;
      this.stopObserving();
      // Closing without Apply drops the changes; reopening shows (and the button counts) the applied ones.
      // The inactive switch goes back too: left on, it would hide an applied inactive group it had
      // deselected, which would then stay selected unseen and be sent again on the next Apply.
      if (!this.applying) {
        this.selectedIds = (this.selected || []).map(({ id }) => id);
        this.hideInactive = this.hideInactiveOnOpen;
      }
    },
    resetRows() {
      this.rowLimit = ROWS_PER_BATCH;
      if (this.$refs.list) this.$refs.list.scrollTop = 0;
    },
    observeSentinel() {
      const { list, sentinel } = this.$refs;
      if (!list || !sentinel) {
        this.stopObserving();
        return;
      }
      // The list element is recreated each time the dropdown opens, so the observer is tied to it.
      if (!this.observer || this.observer.root !== list) {
        this.stopObserving();
        this.observer = new IntersectionObserver(this.onSentinelIntersect, { root: list, rootMargin: '200px' });
      }
      this.observer.disconnect();
      this.observer.observe(sentinel);
    },
    stopObserving() {
      this.observer?.disconnect();
      this.observer = null;
    },
    onSentinelIntersect(entries) {
      if (entries.some(({ isIntersecting }) => isIntersecting)) this.rowLimit += ROWS_PER_BATCH;
    },
    onSearchInput(text) {
      this.searchText = text;
      this.applySearch(text);
    },
    toggleWorkgroup(id, checked) {
      this.selectedIds = checked
        ? [...this.selectedIds, id]
        : this.selectedIds.filter((selectedId) => selectedId !== id);
    },
    toggleCampus(workgroups, checked) {
      const ids = new Set(workgroups.map(({ id }) => id));
      const otherIds = this.selectedIds.filter((id) => !ids.has(id));
      this.selectedIds = checked ? [...otherIds, ...ids] : otherIds;
    },
    // 'all' | 'some' | 'none' for a template group's route groups.
    selectionState(workgroups) {
      const selectedCount = workgroups.filter(({ id }) => this.selectedIdSet.has(id)).length;
      if (selectedCount === workgroups.length) return 'all';
      return selectedCount ? 'some' : 'none';
    },
    // A search opens every template group, so matching route groups aren't hidden in a collapsed one.
    isTemplateGroupOpen(key) {
      return !!this.filterText.trim() || this.openTemplateGroups.includes(key);
    },
    toggleTemplateGroup(key) {
      this.openTemplateGroups = this.openTemplateGroups.includes(key)
        ? this.openTemplateGroups.filter((openKey) => openKey !== key)
        : [...this.openTemplateGroups, key];
    },
    isCollapsed(campusId) {
      return this.collapsedCampusIds.includes(campusId);
    },
    toggleCollapse(campusId) {
      this.collapsedCampusIds = this.isCollapsed(campusId)
        ? this.collapsedCampusIds.filter((id) => id !== campusId)
        : [...this.collapsedCampusIds, campusId];
    },
    // Selected ids with no row (not in the loaded list, e.g. restored from the URL) are kept as { id }
    // rather than silently dropped.
    apply() {
      const shown = this.groups.flatMap(({ workgroups }) => workgroups).filter(({ id }) => this.selectedIdSet.has(id));
      const shownIds = new Set(shown.map(({ id }) => id));
      const unmatched = this.selectedIds.filter((id) => !shownIds.has(id)).map((id) => ({ id }));
      // Read before hiding: Bootstrap fires hidden synchronously, and onHidden must not undo this selection.
      this.applying = true;
      Dropdown.getOrCreateInstance(this.$refs.toggle).hide();
      this.applying = false;
      this.$emit('select', [...shown, ...unmatched]);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/sass/common/mixins';

.search-workgroup {
  @include thin-scrollbar;
}

// The spinner takes the caret's place while the workgroups load.
.dropdown-toggle.is-loading::after {
  display: none;
}

// Keeps Bootstrap's 2:1 switch ratio so the knob stays round.
.inactive-switch {
  height: 20px;
  width: 40px !important;
}

.search-icon {
  left: 14px;
  color: var(--color-text-muted);
  pointer-events: none;
}

.search-input {
  padding-left: 40px;
}

.collapse-icon {
  font-size: 12px;
  color: var(--color-text-muted);
}

// Replaces the global `.search-workgroup .dropdown-menu` cap (294px) with 650px. Open, the menu is a column:
// the search, switch and footer keep their height and .item-list scrolls in the rest.
.workgroup-menu {
  max-height: 650px !important;

  &.show {
    display: flex;
    flex-direction: column;
  }
}

.item-list {
  flex: 1 1 auto;
  // Lets the list shrink below its content inside the capped menu, so it scrolls instead of overflowing.
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
}

// Pins the campus row to the top of .item-list while its route groups are in view. Sticky is
// bounded by the campus block, so the next campus pushes it out instead of stacking on top.
.campus-header {
  position: sticky;
  top: 0;
  z-index: 1;
  background-color: var(--color-surface-raised);
}

.campus-name {
  font-weight: bolder;
  word-wrap: break-word;
  width: 80%;
  display: inline-block;
}
.sticky-top {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 4px;
}
</style>
